import { useState, useEffect } from "react";
import { isDiscordActivity } from "../utils/discord";

export function useMobileLayout({ isDiscord, canvasHostRef }) {
  const [isMobile, setIsMobile] = useState(false);
  const [viewportInsets, setViewportInsets] = useState({ top: 0, bottom: 0 });
  const [statsBarHeight, setStatsBarHeight] = useState(0);
  const [mobileDockHeight, setMobileDockHeight] = useState(0);
  const [stageViewport, setStageViewport] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateMobile = () => {
      const coarse =
        window.matchMedia &&
        window.matchMedia("(pointer: coarse)").matches;
      const portrait =
        window.innerHeight >= window.innerWidth &&
        window.innerWidth < 992;
      setIsMobile(isDiscord || coarse || window.innerWidth < 768 || portrait);
    };
    updateMobile();
    const mediaQuery =
      window.matchMedia && window.matchMedia("(pointer: coarse)");
    const onChange = () => updateMobile();
    mediaQuery?.addEventListener?.("change", onChange);
    window.addEventListener("resize", updateMobile);
    return () => {
      window.removeEventListener("resize", updateMobile);
      mediaQuery?.removeEventListener?.("change", onChange);
    };
  }, [isDiscord]);

  useEffect(() => {
    const updateInsets = () => {
      const viewport = window.visualViewport;
      if (!viewport) {
        setViewportInsets({ top: 0, bottom: 0 });
        return;
      }
      const top = Math.max(0, Math.round(viewport.offsetTop || 0));
      const bottom = Math.max(
        0,
        Math.round(window.innerHeight - (viewport.offsetTop + viewport.height))
      );
      setViewportInsets((prev) =>
        prev.top === top && prev.bottom === bottom ? prev : { top, bottom }
      );
    };

    updateInsets();
    const viewport = window.visualViewport;
    window.addEventListener("resize", updateInsets);
    viewport?.addEventListener("resize", updateInsets);
    viewport?.addEventListener("scroll", updateInsets);

    return () => {
      window.removeEventListener("resize", updateInsets);
      viewport?.removeEventListener("resize", updateInsets);
      viewport?.removeEventListener("scroll", updateInsets);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const preventSelection = (event) => {
      const target = event.target;
      const tagName = target?.tagName?.toLowerCase?.() || "";
      const isEditable =
        tagName === "input" ||
        tagName === "textarea" ||
        target?.isContentEditable;
      if (isEditable) return;
      event.preventDefault();
    };

    const preventContextMenu = (event) => {
      event.preventDefault();
    };

    document.addEventListener("selectstart", preventSelection);
    document.addEventListener("contextmenu", preventContextMenu);

    return () => {
      document.removeEventListener("selectstart", preventSelection);
      document.removeEventListener("contextmenu", preventContextMenu);
    };
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) {
      setMobileDockHeight(0);
    }
  }, [isMobile]);

  useEffect(() => {
    const host = canvasHostRef.current;
    if (!host) return;

    let frameId = 0;
    const measure = () => {
      frameId = 0;
      const rect = host.getBoundingClientRect();
      const width = Math.max(1, Math.floor(rect.width));
      const height = Math.max(1, Math.floor(rect.height));
      setStageViewport((prev) => {
        if (prev.width === width && prev.height === height) return prev;
        return { width, height };
      });
    };
    const scheduleMeasure = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(measure);
    };

    scheduleMeasure();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(host);
    }

    const viewport = window.visualViewport;
    window.addEventListener("resize", scheduleMeasure);
    viewport?.addEventListener("resize", scheduleMeasure);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      window.removeEventListener("resize", scheduleMeasure);
      viewport?.removeEventListener("resize", scheduleMeasure);
      resizeObserver?.disconnect();
    };
  }, []);

  // Computed layout offsets
  const discordHeaderCompensation =
    isMobile && isDiscord ? Math.ceil(statsBarHeight) : 0;
  const discordTopOffset = isMobile
    ? viewportInsets.top + discordHeaderCompensation
    : 0;
  const discordBottomOffset = isMobile
    ? (isDiscord ? 0 : viewportInsets.bottom)
    : 0;
  const mobileDockReservedHeight = isMobile ? mobileDockHeight : 0;
  const controlButtonsTopOffset = isMobile
    ? discordTopOffset + statsBarHeight + 8
    : discordTopOffset + 8;
  const arrowPanelTopOffset = isMobile
    ? discordTopOffset + statsBarHeight + 8
    : 0;
  const arrowPanelBottomOffset = isMobile
    ? discordBottomOffset + mobileDockReservedHeight
    : 0;
  const contextPanelBottomOffset = isMobile
    ? discordBottomOffset + mobileDockReservedHeight + 12
    : 0;
  const topScrimHeight = isMobile ? Math.max(0, discordTopOffset) : 0;

  return {
    isMobile,
    viewportInsets,
    statsBarHeight,
    setStatsBarHeight,
    mobileDockHeight,
    setMobileDockHeight,
    stageViewport,
    discordTopOffset,
    discordBottomOffset,
    mobileDockReservedHeight,
    controlButtonsTopOffset,
    arrowPanelTopOffset,
    arrowPanelBottomOffset,
    contextPanelBottomOffset,
    topScrimHeight,
  };
}
